import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { MapPin, Check } from 'lucide-react';
import './FixtureFinder.css';

/**
 * FixtureFinderSettingsCard — Club Settings → "Fixture Finder" (B-43.1, v45).
 * Admins only (the Settings tab is admin-only, and the API checks too).
 *
 * One switch: list this club in other clubs' "Clubs near you". Listed by
 * default. Hiding only takes the club out of other clubs' lists; its own
 * admins, team managers and coaches can still look.
 */
export default function FixtureFinderSettingsCard({ clubSlug }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['fixture-finder-settings', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/fixture-finder/settings`).then(r => r.data),
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const setListed = async (listed) => {
    setError(''); setSaved(false); setSaving(true);
    try {
      const res = await axios.patch(`/clubs/${clubSlug}/fixture-finder/settings`, { hidden: !listed });
      qc.setQueryData(['fixture-finder-settings', clubSlug], res.data);
      qc.invalidateQueries({ queryKey: ['fixture-finder', clubSlug] });
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="card settings-card" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}><MapPin size={18} /> Fixture Finder</h3>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Helps clubs find a game. Other verified clubs of your sport nearby see your club's
        <strong> name, town and distance</strong> in their <strong>Clubs near you</strong> list, and can open
        your public club page. Nothing about your members is shared.
      </p>
      {isLoading && <div className="page-loading" style={{ minHeight: 60 }}><div className="spinner" /></div>}
      {data && (
        <>
          <label className="ff-setting">
            <input type="checkbox" checked={!data.hidden} disabled={saving}
              onChange={e => setListed(e.target.checked)} />
            {data.hidden ? 'Hidden from other clubs' : 'Listed for other clubs nearby'}
          </label>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 0 28px' }}>
            Hiding your club doesn't stop your own staff using Fixture Finder.
          </p>
          {error && <div className="ff-note is-error">{error}</div>}
          {saved && !saving && (
            <span style={{ fontSize: 13, color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <Check size={14} /> Saved
            </span>
          )}
        </>
      )}
    </div>
  );
}
